import React, { MouseEvent, useCallback, useMemo } from 'react';
import noop from 'lodash/noop';
import Heading from '../Heading';
import Tooltip from '../Tooltip';
import SparklineChart from '../SparklineChart';
import TrendIndicator from '../TrendIndicator';
import Text from '../Text';
import Loader from '../Loader';
import { st, classes } from './AnalyticsSummaryCard.st.css';
import {
  dataHooks,
  HORIZONTAL_PADDING,
} from './AnalyticsSummaryCard.constants';
import { InjectedFocusableProps, withFocusable } from '../common/Focusable';
import { useHover } from '../common/useHover';
import { AnalyticsSummaryCardProps } from './AnalyticsSummaryCard.types';

const SPARKLINE_HEIGHT = 22;

// storybook utils can't unwrap HOC, hence the export
export const AnalyticsSummaryCard = ({
  isLoading = false,
  ctaButton = null,
  footer = null,
  onChartHover = noop,
  isTrendVisible = false,
  alwaysShowCTA = false,
  chartWidth = 69,
  chartAnimationDuration = 300,
  onClick,
  onCTAClick,
  title,
  titleTooltip,
  value,
  valueTooltip,
  dataHook,
  className,
  trend,
  invertedTrend,
  chartData,
  chartColorHex,
  chartHighlightedStartingIndex,
  getChartTooltipContent,
  focusableOnFocus,
  focusableOnBlur,
  border = false,
  horizontalPadding = HORIZONTAL_PADDING.small,
}: InjectedFocusableProps<AnalyticsSummaryCardProps, HTMLDivElement>) => {
  const { hovered: isHovered, hoverProps } = useHover();

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => onClick?.(event),
    [onClick],
  );

  const handleCTAClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      onCTAClick?.(event);
    },
    [onCTAClick],
  );

  const shouldShowCTA = useMemo(
    () => !isLoading && ctaButton && (isHovered || alwaysShowCTA),
    [alwaysShowCTA, ctaButton, isHovered, isLoading],
  );

  const renderTitle = useCallback(
    () => (
      <Tooltip
        placement="top"
        content={titleTooltip}
        disabled={!titleTooltip}
        dataHook={dataHooks.analyticsSummaryCardTitleTooltip}
      >
        <span data-hook={dataHooks.analyticsSummaryCardTitle}>{title}</span>
      </Tooltip>
    ),
    [title, titleTooltip],
  );

  const renderValue = useCallback(() => {
    const heading = (
      <Heading size="large" dataHook={dataHooks.analyticsSummaryCardValue}>
        {value}
      </Heading>
    );

    if (valueTooltip) {
      return (
        <Tooltip
          placement="top"
          content={valueTooltip}
          dataHook={dataHooks.analyticsSummaryCardValueTooltip}
        >
          {heading}
        </Tooltip>
      );
    }
    return heading;
  }, [value, valueTooltip]);

  const isClickable = useMemo(() => onClick !== undefined, [onClick]);

  return (
    <div
      className={st(
        classes.root,
        {
          hovered: isHovered,
          clickable: isClickable,
          border,
          horizontalPadding,
        },
        className,
      )}
      data-hook={dataHook}
      onClick={handleClick}
      {...hoverProps}
      onFocus={focusableOnFocus}
      onBlur={focusableOnBlur}
    >
      {/* Loader */}
      {isLoading && (
        <div className={classes.loader}>
          <Loader dataHook={dataHooks.analyticsSummaryCardLoader} size="tiny" />
        </div>
      )}

      {/* CTA */}
      {shouldShowCTA && (
        <div
          data-hook={dataHooks.analyticsSummaryCardCTA}
          className={st(classes.ctaButton)}
          onClick={handleCTAClick}
          onFocus={focusableOnFocus}
          onBlur={focusableOnBlur}
        >
          {ctaButton}
        </div>
      )}

      {/* Value line: text column + sparkline */}
      <div className={classes.content}>
        <div className={classes.textContainer}>
          {/* Header */}
          <div
            className={st(classes.title)}
            onFocus={focusableOnFocus}
            onBlur={focusableOnBlur}
          >
            <Text size="tiny" weight="normal">
              {renderTitle()}
            </Text>
          </div>

          {/* Value & Trend */}
          <div className={classes.valueRow}>
            {renderValue()}
            {isTrendVisible && trend !== undefined && (
              <TrendIndicator
                dataHook={dataHooks.analyticsSummaryCardTrend}
                value={trend}
                inverted={invertedTrend}
              />
            )}
          </div>
        </div>

        {/* Sparkline Chart */}
        <div
          className={st(classes.sparkline)}
          onFocus={focusableOnFocus}
          onBlur={focusableOnBlur}
        >
          <SparklineChart
            dataHook={dataHooks.analyticsSummaryCardChart}
            onHover={onChartHover}
            data={chartData}
            color={chartColorHex}
            width={chartWidth}
            height={SPARKLINE_HEIGHT}
            highlightedStartingIndex={chartHighlightedStartingIndex}
            getTooltipContent={getChartTooltipContent}
            animationDuration={chartAnimationDuration}
          />
        </div>
      </div>

      <div className={st(classes.footer)}> {footer}</div>
    </div>
  );
};

AnalyticsSummaryCard.displayName = 'AnalyticsSummaryCard';

export default withFocusable<AnalyticsSummaryCardProps>(AnalyticsSummaryCard);
